import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import StatCard from '../../components/StatCard.jsx';
import StatusBadge from '../../components/StatusBadge.jsx';
import PieChart from '../../components/PieChart.jsx';
import DashboardCard from '../../components/DashboardCard.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { ComplaintsAPI } from '../../lib/api';
import { complaintSerial } from '../../lib/complaintSerial';
import { countNounLabel, pendingComplaintCount } from '../../lib/pendingCounts';
import { useAuth } from '../../lib/auth.jsx';
import { buildPath, statusMeta, complaintCapabilities } from '../../config';

/** Complaints home — mirrors templates/complaints_management_system/dashboard.html. */
export default function ComplaintsDashboard() {
  const { user } = useAuth();
  const can = complaintCapabilities(user);

  const [summary, setSummary] = useState(null);
  const [teamSummary, setTeamSummary] = useState(null);

  const [mine, setMine] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The unscoped ("team") queue would duplicate "mine" for an
        // external user, so skip those calls and don't render that panel.
        const requests = [
          ComplaintsAPI.summary({ mine: 1 }),
          ComplaintsAPI.list({ mine: 1, page_size: 5 }),
        ];
        if (can.canSeeTeam) {
          requests.push(ComplaintsAPI.list({ page_size: 5 }));
          requests.push(ComplaintsAPI.summary({}));
        }

        const [sum, m, t, teamSum] = await Promise.all(requests);
        if (cancelled) return;
        setSummary(sum);
        setMine(m.results || m);
        if (t) setTeam(t.results || t);
        if (teamSum) setTeamSummary(teamSum);

      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [can.canSeeTeam]);


  if (loading) return <Loader label="Loading complaints…" />;

  const counts = (summary && summary.by_status) || {};
  const total = (summary && summary.total) || 0;
  const minePending = pendingComplaintCount(summary);
  const teamPending = pendingComplaintCount(teamSummary);
  const minePendingLabel = countNounLabel(minePending, 'pending complaint');
  const teamPendingLabel = countNounLabel(teamPending, 'pending complaint');

  return (
    <>
      <PageHeader
        title={can.isExternal ? 'My Complaints' : 'Complaint Dashboard'}
        actions={<Link className="ty-btn" to="/complaints/create">+ Create Complaint</Link>}
      />

      <ErrorBox error={error} />

      {/* Stat tile colours follow the original Taskose semantic palette
          (--primary / --warning / --info / --success / --danger).

          Each tile links into the complaints list, with ``?status=…`` on
          the per-status ones so the list opens showing exactly the rows
          the tile counted (``ComplaintsList`` reads the status from the
          query string).  ``my_complaints`` is used because that's the
          scope the summary is computed for — the tile and the page it
          opens must not disagree about the number.

          Counts sit at the top-right of each tile (see ``StatCard``). */}
      <div className="ty-stats">
        <StatCard label="Total"       value={total}                     accent="#7980ff"
                  to={buildPath.complaintsList('my_complaints')} />
        <StatCard label="Pending"     value={counts.pending || 0}        accent="#fcdb66"
                  to={`${buildPath.complaintsList('my_complaints')}?status=pending`} />
        <StatCard label="In Progress" value={counts.in_progress || 0}    accent="#34c8de"
                  to={`${buildPath.complaintsList('my_complaints')}?status=in_progress`} />
        <StatCard label="Resolved"    value={counts.resolved || 0}       accent="#5fd788"
                  to={`${buildPath.complaintsList('my_complaints')}?status=resolved`} />
        <StatCard label="Dropped"     value={counts.drop || 0}           accent="#f05a5a"
                  to={`${buildPath.complaintsList('my_complaints')}?status=drop`} />
        <StatCard label="Closed"      value={counts.closed || 0}         accent="#1b325f"
                  to={`${buildPath.complaintsList('my_complaints')}?status=closed`} />
      </div>

      <div className="ty-grid-2">
        <ComplaintList
          title={can.isExternal ? 'My Recent Complaints' : 'Complaints Assigned To Me'}
          items={mine}
          filter="my_complaints"
          pending={minePending}
          pendingLabel={minePendingLabel}
        />
        {can.canSeeTeam && (
          <ComplaintList
            title="Complaints Assigned To My Team"
            items={team}
            filter="my_team_complaints"
            pending={teamPending}
            pendingLabel={teamPendingLabel}
          />
        )}
      </div>

      {/* Donut chart of complaints by status — equivalent of the
          Highcharts pie used in the original Django dashboard. */}
      <div className="ty-grid-2 ty-mt-3">
        <DashboardCard
          title="My Complaints Chart View"
          count={minePending}
          countLabel={minePendingLabel}
          divider={false}
        >
          <PieChart
            data={Object.entries(counts).map(([status, value]) => {
              const m = statusMeta('complaint', status);
              return { name: m.label, value, color: m.colour };
            })}
          />
        </DashboardCard>
        {/* Same team-scoped summary as the list on the left — only
            meaningful for staff who actually have a team queue. */}
        {can.canSeeTeam && (
          <DashboardCard
            title="Team Complaints Chart View"
            count={teamPending}
            countLabel={teamPendingLabel}
            divider={false}
          >
            <PieChart
              data={Object.entries((teamSummary && teamSummary.by_status) || {}).map(([status, value]) => {
                const m = statusMeta('complaint', status);
                return { name: m.label, value, color: m.colour };
              })}
            />
          </DashboardCard>
        )}
      </div>

    </>
  );
}

function ComplaintList({ title, items, filter, pending, pendingLabel }) {
  return (
    <DashboardCard title={title} count={pending} countLabel={pendingLabel}>
      {items.length === 0 && <div className="ty-empty">No complaints yet.</div>}
      {items.map((c) => {
        const serial = complaintSerial(c);
        return (
          <div className="ty-dash-item" key={c.id}>
            <div className="ty-dash-item__top">
              <Link to={buildPath.complaintDetails(c.id)} className="ty-dash-item__title">
                {c.complaint_title || c.division_name}
              </Link>
              <span className="ty-dash-item__num" title={`Complaint #${serial}`}>
                #{serial}
              </span>
            </div>
            <div className="ty-dash-item__meta">
              <StatusBadge domain="complaint" value={c.status} />
            </div>
          </div>
        );
      })}
      <div style={{ textAlign: 'center', marginTop: 8 }}>
        <Link to={buildPath.complaintsList(filter)}>Show all →</Link>
      </div>
    </DashboardCard>
  );
}
